import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig'; // 包含 CSRF 設定
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';

function AdminUpdateLog({ darkMode }) {
  const [logs, setLogs] = useState([]); // 儲存日誌數據
  const [editingLog, setEditingLog] = useState(null); // 當前正在編輯的日誌
  const [version, setVersion] = useState(''); // 版本號
  const [description, setDescription] = useState(''); // 描述
  const { t } = useTranslation(); // i18n 翻譯
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  // 獲取日誌數據
  useEffect(() => {
    const fetchUpdateLogs = async () => {
      showLoading();
      try {
        const response = await axios.get(`${API_URL}/api/admin/update-logs`, { withCredentials: true });
        setLogs(response.data);
      } catch (error) {
        console.error('獲取更新日誌時發生錯誤：', error);
        if (error.response && error.response.status === 401) {
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          alert(t('accessDenied'));
          navigate('/');
        }
      } finally {
        hideLoading();
      }
    };
    
    fetchUpdateLogs();
  }, [navigate, t, showLoading, hideLoading]);

  const handleEdit = (log) => {
    setEditingLog(log);
    setVersion(log.version);
    setDescription(log.description);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!version || !description) {
      toast.error(t('fillAllFields'));
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/update-logs/${editingLog._id}`,
        { version, description },
        { withCredentials: true }
      );
      setLogs((prevLogs) =>
        prevLogs.map((log) => (log._id === editingLog._id ? response.data : log))
      );
      setEditingLog(null);
      setVersion('');
      setDescription('');
      toast.success(t('logUpdatedSuccessfully'));
    } catch (error) {
      console.error('Error updating log:', error);
      toast.error(t('updateLogError'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!version || !description) {
      toast.error(t('fillAllFields'));
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/update-logs`,
        { version, description },
        { withCredentials: true }
      );
      setLogs([response.data, ...logs]); // 新增日誌插入到最前面
      setVersion('');
      setDescription('');
      toast.success(t('logAddedSuccessfully'));
    } catch (error) {
      console.error('新增更新日誌失敗：', error);
      toast.error(t('addLogError'));
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-black'}`}>
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold mb-4">{t('adminUpdateLog')}</h1>

        {/* 新增或編輯日誌表單 */}
        <form onSubmit={editingLog ? handleUpdate : handleSubmit} className="space-y-4 mb-6">
          <div>
            <label htmlFor="version" className="block text-lg font-medium">
              {t('enterVersion')}
            </label>
            <input
              type="text"
              id="version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className={`w-full p-3 rounded border ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-lg font-medium">
              {t('enterDescription')}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full p-3 rounded border resize-none ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}
              rows="5"
            ></textarea>
          </div>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-3 rounded-md shadow"
          >
            {editingLog ? t('update') : t('submit')}
          </button>
        </form>

        {/* 顯示所有日誌 */}
        <div className="bg-white dark:bg-gray-800 rounded-md shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4">{t('updateLogHistory')}</h2>
          {logs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {logs.map((log) => (
                <div key={log._id} className="border p-4 rounded-lg dark:bg-gray-700 dark:text-white">
                  <h3 className="text-xl font-semibold">{t('version')}: {log.version}</h3>
                  <p>{t('description')}: {log.description}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('lastEdited')}: {log.lastEditedAt ? new Date(log.lastEditedAt).toLocaleDateString() : t('never')}
                  </p>
                  <button
                    onClick={() => handleEdit(log)}
                    className="bg-yellow-500 text-white px-3 py-2 rounded-md mt-2"
                  >
                    {t('edit')}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p>{t('noLogsFound')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminUpdateLog;
